/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";

import { useLayout } from "@/../_metronic/layout/core";
import {
	DetailedIndicatorC1Data,
	getDetailedIndicatorC1,
} from "@/services/esus/indicator/esf/c1/getDetailedIndicatorC1";
import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import {
	getResumeIndicatorC1,
	ResumeIndicatorC1Data,
} from "@/services/esus/indicator/esf/c1/getResumeIndicatorC1";
import { Paginated } from "@/@types/generics/paginated";
import { useDebounce } from "@/hooks/useDebounce";
import {
	getResumeIndicatorC1ByHealthTeam,
	ResumeIndicatorC1ByHealthTeamData,
} from "@/services/esus/indicator/esf/c1/getResumeIndicatorC1ByHealthTeam";

import { IndicatorEsfC1Resume } from "./IndicatorEsfC1Resume";
import { IndicatorEsfC1Attendances } from "./IndicatorEsfC1Attendances";
import { IndicatorEsfC1HealthTeams } from "./IndicatorEsfC1HealthTeams";

type IndicatorEsfC1Props = {
	selectedQuarter: string;
	selectedYear: number;
	selectedMonth: number;
	selectedIne?: string;
	isPendingToUpdate: boolean;
	setIsPendingToUpdate: (isPendingToUpdate: boolean) => void;
};

export function IndicatorEsfC1({
	selectedQuarter,
	selectedYear,
	selectedMonth,
	selectedIne,
	isPendingToUpdate,
	setIsPendingToUpdate,
}: Readonly<IndicatorEsfC1Props>) {
	const { setTitle } = useLayout();
	const { uf, ibgeCode } = useCurrentAccount();
	const pageSize = 10;
	const [resumeIndicatorC1, setResumeIndicatorC1] = useState<ResumeIndicatorC1Data>(
		new ResumeIndicatorC1Data()
	);

	const [pageNumberAttendances, setPageNumberAttendances] = useState(1);
	const [searchAttendance, setSearchAttendance] = useState("");
	const debouncedSearchAttendance = useDebounce(searchAttendance, 2000);
	const [paginatedAttendances, setPaginatedAttendances] = useState(
		new Paginated<DetailedIndicatorC1Data>()
	);
	const [isLoadingAttendances, setIsLoadingAttendances] = useState(false);

	const [pageNumberHealthTeamsClassifications, setPageNumberHealthTeamsClassifications] =
		useState(1);
	const [searchHealthTeamsClassification, setSearchHealthTeamsClassification] = useState("");
	const debouncedSearchHealthTeamsClassifications = useDebounce(
		searchHealthTeamsClassification,
		2000
	);
	const [paginatedHealthTeamsClassifications, setPaginatedHealthTeamsClassifications] = useState(
		new Paginated<ResumeIndicatorC1ByHealthTeamData>()
	);
	const [isLoadingHealthTeamsClassifications, setIsLoadingHealthTeamsClassifications] =
		useState(false);

	async function handleFilter() {
		await Promise.all([
			handleGetResumeIndicatorC1(),
			handleGetResumeIndicatorC1ByHealthTeam(),
			handleGetDetailedIndicatorC1(),
		]);
		setIsPendingToUpdate(false);
	}

	async function handleGetResumeIndicatorC1() {
		setResumeIndicatorC1(
			await getResumeIndicatorC1({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
			})
		);
	}

	async function handleGetResumeIndicatorC1ByHealthTeam(pageNumber?: number) {
		setIsLoadingHealthTeamsClassifications(true);
		setPaginatedHealthTeamsClassifications(
			await getResumeIndicatorC1ByHealthTeam({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchHealthTeamsClassification,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingHealthTeamsClassifications(false);
	}

	async function handleGetAllResumeIndicatorC1ByHealthTeam() {
		return (
			await getResumeIndicatorC1ByHealthTeam({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchHealthTeamsClassification,
				pageNumber: 1,
				pageSize: paginatedHealthTeamsClassifications.totalRecords,
				export: true,
			})
		).data;
	}

	async function handleGetDetailedIndicatorC1(pageNumber?: number) {
		setIsLoadingAttendances(true);
		setPaginatedAttendances(
			await getDetailedIndicatorC1({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchAttendance,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingAttendances(false);
	}

	async function handleGetAllDetailedIndicatorC1() {
		return (
			await getDetailedIndicatorC1({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchAttendance,
				pageNumber: 1,
				pageSize: paginatedAttendances.totalRecords,
				export: true,
			})
		).data;
	}

	useEffect(() => {
		if (!isPendingToUpdate) handleGetDetailedIndicatorC1(pageNumberAttendances);
	}, [pageNumberAttendances]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberAttendances === 1) {
				handleGetDetailedIndicatorC1(pageNumberAttendances);
			} else {
				setPageNumberAttendances(1);
			}
		}
	}, [debouncedSearchAttendance]);

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetResumeIndicatorC1ByHealthTeam(pageNumberHealthTeamsClassifications);
	}, [pageNumberHealthTeamsClassifications]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberHealthTeamsClassifications === 1) {
				handleGetResumeIndicatorC1ByHealthTeam(pageNumberHealthTeamsClassifications);
			} else {
				setPageNumberHealthTeamsClassifications(1);
			}
		}
	}, [debouncedSearchHealthTeamsClassifications]);

	useEffect(() => {
		if (isPendingToUpdate) handleFilter();
	}, [isPendingToUpdate]);

	useEffect(() => {
		setTitle("C1 - Mais Acesso APS");
	}, [setTitle]);

	return (
		<>
			<IndicatorEsfC1Resume resume={resumeIndicatorC1} />
			<IndicatorEsfC1HealthTeams
				paginatedHealthTeamsClassifications={paginatedHealthTeamsClassifications}
				isLoading={isLoadingHealthTeamsClassifications}
				search={searchHealthTeamsClassification}
				setSearch={setSearchHealthTeamsClassification}
				selectedQuarter={selectedQuarter}
				pageNumber={pageNumberHealthTeamsClassifications}
				setPageNumber={setPageNumberHealthTeamsClassifications}
				handleGetAllResumeIndicatorC1ByHealthTeam={
					handleGetAllResumeIndicatorC1ByHealthTeam
				}
			/>
			<IndicatorEsfC1Attendances
				paginatedAttendances={paginatedAttendances}
				isLoading={isLoadingAttendances}
				search={searchAttendance}
				setSearch={setSearchAttendance}
				selectedQuarter={selectedQuarter}
				pageNumber={pageNumberAttendances}
				setPageNumber={setPageNumberAttendances}
				handleGetAllDetailedIndicatorC1={handleGetAllDetailedIndicatorC1}
			/>
		</>
	);
}
